import { LEFT_EAR_TYPE, RIGHT_EAR_TYPE } from "constants/products";
import { HEARING_AID_PRODUCT_TYPE } from "constants/sales";
import { PatientProduct } from "services/types/patient-products.type";
import {
  formatBackendDateOnly,
  formatDateOnlyValue,
  formatMoneyValue
} from "utils/common";

import { PATIENT_ACTIVE_PRODUCTS_OPTIONS } from "../../merge-tags-options/patient-active-products.utils";
import { allowTableInsidePTag } from "../../merge-tags.utils";
import {
  MergeTagsReplaceHandler,
  MergeTagsReplaceHandlerParams
} from "../merge-tags-values";
import {
  getActiveProductsShortDescriptionHtml,
  getHearingAidsTableHtml
} from "./hearing-aids-table.utils";

export const PATIENT_HEATING_AIDS_VALUES: MergeTagsReplaceHandler = {
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.ACTIVE_PRODUCTS_WITH_WARRANTIES]: ({
    data
  }) => {
    if (!data.patientProducts) return null;
    return allowTableInsidePTag(
      getHearingAidsTableHtml({
        products: data.patientProducts,
        addWarranties: true
      })
    );
  },
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.ACTIVE_PRODUCTS]: ({ data }) => {
    if (!data.patientProducts) return null;
    return allowTableInsidePTag(
      getHearingAidsTableHtml({ products: data.patientProducts })
    );
  },
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.ACTIVE_PRODUCTS_SHORT_DESCRIPTION]: ({
    data
  }) => {
    if (!data.patientProducts) return null;
    return allowTableInsidePTag(
      getActiveProductsShortDescriptionHtml(data.patientProducts)
    );
  },
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.ACTIVE_PRODUCTS_WITH_STATUS]: ({ data }) => {
    if (!data.patientProducts) return null;
    return allowTableInsidePTag(
      getHearingAidsTableHtml({
        products: data.patientProducts,
        addStatus: true
      })
    );
  },
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.LEFT_MANUFACTURER]: (params) => {
    const product = getHearingAid(params, LEFT_EAR_TYPE);

    return product?.inventory_product?.product?.master_product.manufacturer
      ?.name;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.LEFT_MODEL]: (params) => {
    const product = getHearingAid(params, LEFT_EAR_TYPE);

    return product?.inventory_product?.product?.master_product.model;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.LEFT_SERIAL_NUMBER]: (params) => {
    const product = getHearingAid(params, LEFT_EAR_TYPE);

    return product?.inventory_product?.serial_number;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.RIGHT_MANUFACTURER]: (params) => {
    const product = getHearingAid(params, RIGHT_EAR_TYPE);

    return product?.inventory_product?.product?.master_product.manufacturer
      ?.name;
  },
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.RIGHT_MODEL]: (params) => {
    const product = getHearingAid(params, RIGHT_EAR_TYPE);

    return product?.inventory_product?.product?.master_product.model;
  },
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.RIGHT_SERIAL_NUMBER]: (params) => {
    const product = getHearingAid(params, RIGHT_EAR_TYPE);

    return product?.inventory_product?.serial_number;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.LEFT_TRIAL_DATE_EXP]: (params) => {
    const product = getHearingAid(params, LEFT_EAR_TYPE);
    const date = product?.due_date;
    return date ? formatDateOnlyValue(date, "MMM dd, yyyy") : undefined;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.RIGHT_TRIAL_DATE_EXP]: (params) => {
    const product = getHearingAid(params, RIGHT_EAR_TYPE);
    const date = product?.due_date;
    return date ? formatDateOnlyValue(date, "MMM dd, yyyy") : undefined;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.LEFT_REPAIR_WARRANTY_EXP]: (params) => {
    const product = getHearingAid(params, LEFT_EAR_TYPE);
    const date =
      product?.manufacturer_warranty_expiration_date ||
      product?.inventory_product?.manufacturer_warranty_expiration_date;
    return date ? formatDateOnlyValue(date, "MMM dd, yyyy") : undefined;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.RIGHT_REPAIR_WARRANTY_EXP]: (params) => {
    const product = getHearingAid(params, RIGHT_EAR_TYPE);
    const date =
      product?.manufacturer_warranty_expiration_date ||
      product?.inventory_product?.manufacturer_warranty_expiration_date;
    return date ? formatDateOnlyValue(date, "MMM dd, yyyy") : undefined;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.LEFT_LAND_WARRANTY_EXP]: (params) => {
    const product = getHearingAid(params, LEFT_EAR_TYPE);
    const date =
      product?.loss_damage_warranty_expiration_date ||
      product?.inventory_product?.loss_damage_warranty_expiration_date;
    return date ? formatDateOnlyValue(date, "MMM dd, yyyy") : undefined;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.RIGHT_LAND_WARRANTY_EXP]: (params) => {
    const product = getHearingAid(params, RIGHT_EAR_TYPE);
    const date =
      product?.loss_damage_warranty_expiration_date ||
      product?.inventory_product?.loss_damage_warranty_expiration_date;
    return date ? formatDateOnlyValue(date, "MMM dd, yyyy") : undefined;
  },

  // Extended Warranty Exp (SN-19389). Two deliberate differences from the MFR/L&D
  // tags above:
  //   1. extended_warranty_expiration_date is a DateTimeField (the others are
  //      DateFields), so it arrives as a midnight-UTC timestamp. formatDateOnlyValue
  //      would fall through to new Date() and render one day early in US timezones —
  //      formatBackendDateOnly corrects for the offset.
  //   2. Renders "N/A" rather than blank when unset, as requested by the customer;
  //      the empty-value arg is why these return a string instead of undefined.
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.LEFT_EXTENDED_WARRANTY_EXP]: (params) => {
    const product = getHearingAid(params, LEFT_EAR_TYPE);
    const date =
      product?.extended_warranty_expiration_date ||
      product?.inventory_product?.extended_warranty_expiration_date;
    return formatBackendDateOnly(date ?? "", "MMM dd, yyyy", "N/A");
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.RIGHT_EXTENDED_WARRANTY_EXP]: (params) => {
    const product = getHearingAid(params, RIGHT_EAR_TYPE);
    const date =
      product?.extended_warranty_expiration_date ||
      product?.inventory_product?.extended_warranty_expiration_date;
    return formatBackendDateOnly(date ?? "", "MMM dd, yyyy", "N/A");
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.LEFT_SERVICE_PLAN_EXP]: (params) => {
    const product = getHearingAid(params, LEFT_EAR_TYPE);
    const date = product?.service_plan_expiration_date;
    return date ? formatDateOnlyValue(date, "MMM dd, yyyy") : undefined;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.RIGHT_SERVICE_PLAN_EXP]: (params) => {
    const product = getHearingAid(params, RIGHT_EAR_TYPE);
    const date = product?.service_plan_expiration_date;
    return date ? formatDateOnlyValue(date, "MMM dd, yyyy") : undefined;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.LEFT_LAND_COST]: (params) => {
    const product = getHearingAid(params, LEFT_EAR_TYPE);
    const amount =
      product?.inventory_product?.product?.loss_and_deductible_amount;
    return amount && Number(amount) !== 0
      ? formatMoneyValue(Number(amount))
      : undefined;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.RIGHT_LAND_COST]: (params) => {
    const product = getHearingAid(params, RIGHT_EAR_TYPE);
    const amount =
      product?.inventory_product?.product?.loss_and_deductible_amount;
    return amount && Number(amount) !== 0
      ? formatMoneyValue(Number(amount))
      : undefined;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.LEFT_DEVICE_COLOR]: (params) => {
    const product = getHearingAid(params, LEFT_EAR_TYPE);
    return product?.inventory_product?.color;
  },

  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.RIGHT_DEVICE_COLOR]: (params) => {
    const product = getHearingAid(params, RIGHT_EAR_TYPE);
    return product?.inventory_product?.color;
  },

  // Delivery Date (Fitting Date) — uses the backend-formatted delivered_at_display
  // to avoid naive-UTC-midnight timezone shift on the client (delivered_at is stored
  // as midnight UTC; new Date() in a US browser rolls it back one calendar day).
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.LEFT_DELIVERY_DATE]: (params) => {
    const product = getHearingAid(params, LEFT_EAR_TYPE);
    return product?.delivered_at_display ?? undefined;
  },
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.RIGHT_DELIVERY_DATE]: (params) => {
    const product = getHearingAid(params, RIGHT_EAR_TYPE);
    return product?.delivered_at_display ?? undefined;
  },
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.DELIVERY_DATE]: (params) => {
    const left =
      getHearingAid(params, LEFT_EAR_TYPE)?.delivered_at_display ?? null;
    const right =
      getHearingAid(params, RIGHT_EAR_TYPE)?.delivered_at_display ?? null;

    if (left && right && left === right) return left;
    if (left && right) return `L: ${left} R: ${right}`;
    if (left) return `L: ${left}`;
    if (right) return `R: ${right}`;
    return undefined;
  },

  // Fit Date — alias of Delivery Date; same delivered_at_display (ENG-1295).
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.LEFT_FIT_DATE]: (params) => {
    const product = getHearingAid(params, LEFT_EAR_TYPE);
    return product?.delivered_at_display ?? undefined;
  },
  [PATIENT_ACTIVE_PRODUCTS_OPTIONS.RIGHT_FIT_DATE]: (params) => {
    const product = getHearingAid(params, RIGHT_EAR_TYPE);
    return product?.delivered_at_display ?? undefined;
  }
};

const getHearingAid = (
  { data, extraData }: MergeTagsReplaceHandlerParams,
  earType: string
): PatientProduct | undefined => {
  // ENG-2498: Patient Forms chooses its device as Anvil does, and only that surface sends `patientForm`.
  if (extraData?.patientForm) return extraData.patientForm.devices[earType];
  return data.patientProducts?.find(
    (product) =>
      product.ear === earType &&
      product.inventory_product?.product?.master_product.type ===
        HEARING_AID_PRODUCT_TYPE
  );
};
